import { TokenType, type Token } from "../types/tokens.js";
import { quotedIdLiteral } from "../formatter/quote-id.js";
import type { TokenCursor } from "./kebab-name.js";
import type { NodeIdPath } from "../types/ast.js";

/**
 * Shared machinery for node references written as dotted paths (#2547,
 * slice A of #2088).
 *
 * Every site that accepts `A.B.C` (import entries, cross-system edge
 * endpoints, cross-domain entity relations, `resource OrderDB.Orders`, the
 * entity `table` mapping) reads the token run through `readNodeIdPathTail`
 * so the sites cannot drift apart lexically (TPL-2088). Like
 * `kebab-name.ts`, this module sees the parser only through the minimal
 * `TokenCursor` view and emits no diagnostics — each call site keeps its
 * own error codes and recovery, which differ deliberately (see the
 * `dangling` contract below).
 *
 * The suffix rule (`nodePathMatchesSuffix` / `resolveNodePathBySuffix`)
 * defines what a path *means*: a reference matches a node when it equals
 * the tail of that node's full path, a bare id being the length-1 case.
 * Existing sites keep their site-specific resolvers; slices B/C (#2548,
 * #2549) resolve through these helpers.
 */

/** Result of reading the dotted tail of a node-reference path. */
export interface NodeIdPathTail {
  /** Segment values actually read: `first.value` plus each accepted tail segment. */
  segments: NodeIdPath;
  /** Last accepted segment token (for building source ranges). */
  end: Token;
  /**
   * Set when a consumed `.` was not followed by a valid segment token: the
   * offending token, deliberately NOT consumed. The caller decides how to
   * report and whether to skip it — recovery differs per site and is part
   * of each site's observable behavior.
   */
  dangling?: Token;
  /**
   * The `.` that was consumed without a segment after it, set with
   * {@link dangling}. It is what a diagnostic should underline: `end` stops at
   * the last valid identifier, so a range built from it squiggles correct text
   * and leaves the offending character unmarked, and `dangling` may sit on a
   * later line (a `}` closing the block).
   */
  danglingDot?: Token;
}

export interface ReadNodeIdPathTailOptions {
  /** Maximum number of segments to read, including `first`. Default: unlimited. */
  maxSegments?: number;
  /** Accept `StringLiteral` tokens as tail segments (default: identifiers only). */
  acceptStringSegments?: boolean;
}

/**
 * Read the `(Dot segment)*` tail of a node-reference path whose first
 * segment token has already been consumed by the caller (the same calling
 * convention as `stitchKebabTail`). A `.` is consumed only while the
 * segment budget allows; if the token after a consumed `.` is not a valid
 * segment, or is the source of an edge on a later line, reading stops and
 * that token is returned as `dangling`, unconsumed.
 */
export function readNodeIdPathTail(
  first: Token,
  cursor: TokenCursor,
  opts?: ReadNodeIdPathTailOptions,
): NodeIdPathTail {
  const maxSegments = opts?.maxSegments ?? Number.POSITIVE_INFINITY;
  const acceptStringSegments = opts?.acceptStringSegments ?? false;
  const segments: NodeIdPath = [first.value];
  let end = first;
  while (segments.length < maxSegments && cursor.peek().type === TokenType.Dot) {
    const dot = cursor.peek();
    cursor.advance(); // consume "."
    const next = cursor.peek();
    const isSegment =
      next.type === TokenType.Identifier ||
      (acceptStringSegments && next.type === TokenType.StringLiteral);
    // An id on a later line that an arrow follows is the next statement's edge
    // source, not this path's next segment. A path segment is never followed
    // by an arrow, so this only changes recovery after a trailing dot (#3499).
    const opensEdge =
      next.loc.line > dot.loc.line &&
      (cursor.peekAt(1).type === TokenType.Arrow ||
        cursor.peekAt(1).type === TokenType.DashedArrow);
    if (!isSegment || opensEdge) {
      return { segments, end, dangling: next, danglingDot: dot };
    }
    cursor.advance();
    segments.push(next.value);
    end = next;
  }
  return { segments, end };
}

/**
 * True when a `<Infra>.<Leaf>` reference (`resource`, the entity `table`
 * mapping) was read past its leaf: a third segment, or a dot after the second.
 * Both sites read the whole path and report this once as
 * `infra-path-too-long`, rather than stopping at two segments and leaving the
 * rest for the block (#3499).
 */
export function pathPastLeaf(tail: NodeIdPathTail): boolean {
  return tail.segments.length > 2 || (tail.segments.length === 2 && tail.dangling !== undefined);
}

/**
 * A reference matches a node when it equals the tail of that node's full
 * path. A bare id is the length-1 case.
 */
export function nodePathMatchesSuffix(
  ref: readonly string[],
  fullPath: readonly string[],
): boolean {
  if (ref.length === 0 || ref.length > fullPath.length) {
    return false;
  }
  const offset = fullPath.length - ref.length;
  for (let i = 0; i < ref.length; i++) {
    if (ref[i] !== fullPath[offset + i]) {
      return false;
    }
  }
  return true;
}

/**
 * Resolve a reference against a candidate set by the suffix rule,
 * returning every match in candidate order. Works over any candidate shape
 * that carries a `path` (reference-validation's `DeclaredNodePath` &c.).
 */
export function resolveNodePathBySuffix<T extends { path: NodeIdPath }>(
  ref: readonly string[],
  candidates: Iterable<T>,
): T[] {
  const matches: T[] = [];
  for (const candidate of candidates) {
    if (nodePathMatchesSuffix(ref, candidate.path)) {
      matches.push(candidate);
    }
  }
  return matches;
}

/**
 * Plain dotted join of a node path, for TEXT only: diagnostic params and
 * messages.
 *
 * The join is not injective: `["Shop", "Api"]` and `["Shop.Api"]` both give
 * `Shop.Api`. Never key anything on it. The path-keyed indices
 * (`ownerIndex`, `teamOwnership`, `boundaryMembership`) used to, and two nodes
 * shared one owner slot until #2819 moved them to {@link nodePathRefId}. For a
 * key whose only job is identity, reach for {@link nodePathIdentityKey}; for
 * TEXT that is *also* an identity (an id two elements must not share, or an
 * index key a consumer reads back as a qualified id), reach for
 * {@link nodePathRefId}, and split it with {@link parseNodePathRefId}.
 */
export function nodePathKey(segments: readonly string[]): string {
  return segments.join(".");
}

/**
 * Injective key for a node path used purely as a map / set key.
 *
 * `nodePathKey` collapses `["a.b"]` and `["a", "b"]` onto one string, which is
 * harmless for text but not for identity: a memo or a dedup set keyed that way
 * answers a question about one ref with the answer computed for another, and
 * which of the two wins depends on declaration order (TPL-1352). Nothing reads
 * this key back as an id, so the encoding is free to be JSON.
 */
export function nodePathIdentityKey(segments: readonly string[]): string {
  return JSON.stringify(segments);
}

/**
 * Injective TEXT form of a node path: the id a consumer both reads back as a
 * qualified id and relies on to name exactly one element.
 *
 * {@link nodePathKey}'s plain join loses the segment boundaries, so
 * `["Weird", "Shop.Api"]` and `["Shop", "Api"]` collapse onto one string. That
 * is harmless where the join is only text, but a deploy container emits it as
 * its id (#2714): two containers answered to `Shop.Api`, the later one won
 * `containerCenterX`, and the SVG carried the id twice. A segment that would
 * make the join ambiguous is therefore wrapped in the `.krs` string-literal
 * form, which restores the boundary the join lost. The result decodes one way
 * only, so the encoding is injective.
 *
 * It quotes strictly less than the formatter does. `quoteId` asks whether a
 * `.krs` token can carry the id bare, so it also quotes kebab ids and reserved
 * keywords; the question here is only whether the join stays readable, and
 * quoting more would rename container ids that have never been ambiguous. The
 * result is therefore NOT a `.krs` reference to re-parse — it is an id whose
 * separators happen to be unambiguous. A non-empty segment with no `.`, `"` or
 * `\` in it is emitted bare, so every such id keeps the spelling it had.
 */
export function nodePathRefId(segments: readonly string[]): string {
  return segments.map(quoteNodePathSegment).join(".");
}

/**
 * Inverse of {@link nodePathRefId}: split the injective text form back into
 * its segments. A quoted segment is the `.krs` string-literal form
 * `quotedIdLiteral` produced (`\\` and `\"` escapes); a bare segment runs to
 * the next `.`. The empty string decodes to no segments, and `nodePathRefId`
 * round-trips through here for every path (#2917: the app and VS Code read a
 * card's `data-node-path` back into the `viewPath` it drills to).
 */
export function parseNodePathRefId(text: string): string[] {
  const segments: string[] = [];
  let i = 0;
  const n = text.length;
  if (n === 0) return segments;
  for (;;) {
    if (text[i] === '"') {
      let value = "";
      i++;
      for (;;) {
        if (i >= n) {
          // Unterminated literal: keep what was read rather than throwing; the
          // attribute is produced by `nodePathRefId`, so this is defensive only.
          break;
        }
        const ch = text[i];
        if (ch === "\\" && i + 1 < n) {
          value += text[i + 1];
          i += 2;
          continue;
        }
        if (ch === '"') {
          i++;
          break;
        }
        value += ch;
        i++;
      }
      segments.push(value);
    } else {
      const end = text.indexOf(".", i);
      const stop = end === -1 ? n : end;
      segments.push(text.slice(i, stop));
      i = stop;
    }
    if (i >= n) return segments;
    // Only a separator may follow a segment.
    if (text[i] === ".") {
      i++;
      if (i >= n) {
        // Trailing separator: an empty bare segment, which `nodePathRefId`
        // would have quoted; keep the decode total anyway.
        segments.push("");
        return segments;
      }
      continue;
    }
    // A quoted segment followed by something other than `.` cannot come from
    // `nodePathRefId`; treat the rest as part of a bare segment to stay total.
    const end = text.indexOf(".", i);
    const stop = end === -1 ? n : end;
    segments[segments.length - 1] += text.slice(i, stop);
    i = stop;
    if (i >= n) return segments;
    i++;
  }
}

/**
 * A segment is quoted exactly when leaving it bare would make the join
 * ambiguous: it carries the `.` separator, or one of the characters the quoted
 * form is built from (`"` / `\`), which a decoder would otherwise read as the
 * start of a literal or as an escape. The empty segment is quoted too — a
 * `service ""` parses, and bare it would vanish from the join entirely.
 */
function quoteNodePathSegment(segment: string): string {
  return segment === "" || /["\\.]/.test(segment) ? quotedIdLiteral(segment) : segment;
}

/** A resolved match that knows the kind of the node it points at. */
export interface KindedPathCandidate {
  kind: string;
  path: NodeIdPath;
}

/**
 * Decide whether a multi-match is ambiguous (#2088): report only when the
 * matches are NOT uniform in `(kind, depth)`. Uniform multi-matches are the
 * parallel-modelling patterns ADR-927 / ADR-1566 legitimize (migration
 * coexistence, multi-tenant, a generic domain name across systems), where
 * broadcast is the intent and renaming is not an available remedy.
 *
 * Returns the candidates when ambiguous, `undefined` otherwise. Order
 * independent by construction: the verdict is a set-size test.
 */
export function ambiguousNodePathCandidates<T extends KindedPathCandidate>(
  matches: readonly T[],
): T[] | undefined {
  if (matches.length < 2) {
    return undefined;
  }
  const keys = new Set(matches.map((m) => `${m.kind}:${m.path.length}`));
  return keys.size > 1 ? [...matches] : undefined;
}

/**
 * The bare-id view of a map keyed by full path ({@link nodePathRefId}): one
 * entry per bare id, so a consumer that only knows an id still gets one node's
 * value rather than a mix of two (#2934).
 *
 * Where several paths in the map end in the same id, the entry is the one
 * `ranking` (`KrsFile.nodePathRanking`) puts first: the `nodePathIndex` winner
 * when it is in the map, otherwise the next ranked path that is (TPL-1583,
 * ADR-2550). A path the ranking does not hold (a kind `nodePathIndex` does not
 * index, such as a system) sorts after the ranked ones, by its key text. The
 * order is taken from the model and never from the map, so the answer does not
 * depend on the order the map was filled in.
 *
 * Every per-node map with one value per node builds its bare view here rather
 * than by its own tie-break, which is how the maps used to disagree on which
 * same-id node they described.
 */
export function bareIdView<V>(
  byPath: ReadonlyMap<string, V>,
  ranking: ReadonlyMap<string, readonly (readonly string[])[]>,
): Map<string, V> {
  const keysById = new Map<string, string[]>();
  for (const key of byPath.keys()) {
    const segments = parseNodePathRefId(key);
    const id = segments[segments.length - 1];
    if (id === undefined) continue;
    const keys = keysById.get(id);
    if (keys === undefined) keysById.set(id, [key]);
    else keys.push(key);
  }
  const view = new Map<string, V>();
  for (const [id, keys] of keysById) {
    view.set(id, byPath.get(keys.length === 1 ? keys[0] : pickRanked(keys, ranking.get(id)))!);
  }
  return view;
}

function pickRanked(
  keys: readonly string[],
  ranked: readonly (readonly string[])[] | undefined,
): string {
  const rankOf = new Map<string, number>();
  for (const [i, path] of (ranked ?? []).entries()) {
    const key = nodePathRefId(path);
    if (!rankOf.has(key)) rankOf.set(key, i);
  }
  let best = keys[0];
  for (const key of keys.slice(1)) {
    const a = rankOf.get(key) ?? Infinity;
    const b = rankOf.get(best) ?? Infinity;
    if (a < b || (a === b && key < best)) best = key;
  }
  return best;
}
